import {
  QuotationFormSchema,
  TravelVoucherFormSchema,
} from "@factory/form/form.rule";
import { GetResponseInvoiceModel } from "@factory/model/query-model-invoice";
import {
  GetPaymentModel,
  GetResponsePaymentModel,
} from "@factory/model/query-model-payment";
import { GetResponseQuotationModel } from "@factory/model/query-model-quotation";
import { useQyGetInvoiceById } from "@factory/query/invoice.query";
import { useQyGetPayment } from "@factory/query/payment.query";
import { useQyGetQuotationById } from "@factory/query/quotation.query";
import TextBlock from "@shared/ui/text-block/text-block";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { LegacyRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

// import { useFormContext } from "react-hook-form";
import { appAsset } from "../../app.asset";

interface TravelVoucherFileProps {
  invoiceId: string;
  fileRef: LegacyRef<HTMLDivElement> | undefined;
}
export function TravelVoucherFile({
  invoiceId,
  fileRef,
}: TravelVoucherFileProps) {
  const { formatDate } = useDateTimeContext();
  const { watch } = useFormContext<TravelVoucherFormSchema>();
  const [formData, setFormData] = useState<QuotationFormSchema>();
  const [quotationId, setQuotationId] = useState<string>("");
  const [invoiceDate, setInvoiceDate] = useState<string>("");
  const [payments, setPayments] = useState<GetPaymentModel[]>();
  const travelVoucherData = watch();
  // const { travelVoucherId } = useParams();
  const [totalPaid, setTotalPaid] = useState(0);

  useQyGetPayment(
    "",
    10,
    0,
    undefined,
    { voucher: travelVoucherData.baseInvoice || "" },
    true,
    (response: GetResponsePaymentModel) => {
      if (!response || response.data?.length === 0) {
        return;
      }
      const total =
        response.data?.reduce((sum, p) => sum + (p.amount ?? 0), 0) || 0;
      setTotalPaid(total);
      setPayments(response?.data);
    }
  );

  useQyGetInvoiceById(invoiceId, (response: GetResponseInvoiceModel) => {
    if (response?.data) {
      setQuotationId(response?.data[0]?.base_quote || "");
      setInvoiceDate(response?.data[0]?.invoice_date);
    }
  });
  useQyGetQuotationById(quotationId, (response: GetResponseQuotationModel) => {
    if (!response || response.data?.length === 0) {
      return;
    }
    const responseData = response?.data?.[0];

    const transportItems = responseData?.transport?.map((item) => {
      const transportItem = {
        quotation: item.quotation,
        transportType: item.transport_type,
        transportTypeName: item.transport_type_name,
        fromLocation: item.from_location,
        fromLocationName: item.from_location_name,
        toLocation: item.to_location,
        toLocationName: item.to_location_name,
        pricePerPax: item.price_per_pax,
        numPeople: item.num_people,
        unit: item.unit,
        amount: item.amount,
      };
      return transportItem;
    });

    const accommodationItems = responseData?.accommodation?.map((item) => {
      const accommodationItem = {
        quotation: item.quotation,
        hotel: item.hotel,
        hotelName: item.hotel_name,
        roomType: item.room_type,
        roomTypeName: item.room_type_name,
        numPeople: item.num_people,
        unit: item.unit,
        validFrom: item.valid_from,
        validUntil: item.valid_until,
        name: item.name,
        price: item.price,
        withBreakfast: item.with_breakfast, // Added for UI purposes
      };
      return accommodationItem;
    });

    const activitiesItems = responseData?.activities?.map((item) => {
      const activitiesItem = {
        quotation: item.quotation,
        baseActivity: item.base_activity,
        baseActivityName: item.base_activity_name,
        isModified: item.is_modified,
        description: item.description,
        day: item.day,
        isChildActivity: item.is_child_activity,
        childRate: item.child_rate,
        showAsOptional: item.show_as_optional,
        isFree: item.is_free,
        entranceFee: item.entrance_fee,
        numPeople: item.num_people,
        unit: item.unit,
        name: item.name,
        ratePerPax: item.rate_per_pax,
        amount: item.amount,
      };
      return activitiesItem;
    });

    const quotationData = {
      basePackage: responseData?.base_package,
      packageName: responseData?.base_package_name,
      client: responseData?.client,
      clientName: responseData?.client_name,
      quotationDate: responseData?.quotation_date,
      location: responseData?.location,
      locationName: responseData?.location_name,
      startDate: formatDate(responseData?.start_date),
      endDate: formatDate(responseData?.end_date),
      numAdults: responseData?.num_adults,
      numChildren: responseData?.num_children,
      numNights: responseData?.num_nights,
      numDays: responseData?.num_days,
      agent: responseData?.agent || "",
      agentMarkup: responseData?.agent_markup,
      amount: responseData?.amount,
      activities: activitiesItems,
      transport: transportItems,
      accommodation: accommodationItems,
      packageInclusions: responseData?.package_inclusions,
    };
    setFormData(quotationData);
  });

  const numDays = formData?.numDays || 0;
  const dayOptions = numDays
    ? Array.from({ length: numDays }, (_, i) => ({
        label: `Day ${i + 1}`,
        value: i + 1,
      }))
    : [];

  return (
    <div
      ref={fileRef}
      className="flex flex-col justify-between h-[14in] bg-white"
    >
      <div className="preview-file px-10 py-5 bg-white space-y-2">
        <section className="flex justify-center">
          <div>
            <img
              src={appAsset.previewLogo}
              alt="TravelVoucher Logo"
              className="w-full"
            />
          </div>
        </section>
        <section className="flex justify-center mb-10">
          <div>
            <h1>TRAVEL VOUCHER</h1>
          </div>
        </section>
        <section className="grid grid-cols-2">
          <div>
            <TextBlock label="Client Name" value={formData?.clientName} />
            <TextBlock label="Destination" value={formData?.locationName} />
            <TextBlock label="Package" value={formData?.packageName} />
          </div>
          <div>
            <TextBlock
              label="Confirmation Number"
              value={travelVoucherData?.confirmationNum}
            />
            <TextBlock label="Booking Date" value={formatDate(invoiceDate)} />
          </div>
        </section>
        <section className="flex">
          <table className="flex-1 w-full text-[0.8em] border border-black">
            <thead>
              <tr className="bg-gray-900 text-white text-left font-bold">
                <th className="pl-4 py-1">TRAVEL DATE/S</th>
                <th className="py-1">DESCRIPTION</th>
                <th className="py-1">RATE PER PERSON</th>
                <th className="py-1">NO. OF PAX</th>
                <th className="py-1">AMOUNT</th>
              </tr>
            </thead>
            <tr>
              <td className="text-center">{`${formatDate(
                formData?.startDate
              )}`}</td>
              <td>
                {`${formData?.numDays}D${formData?.numNights}N ${formData?.packageName}`}
              </td>
              <td>
                {currencyFormat(
                  (formData?.amount || 0) /
                    ((formData?.numAdults || 0) + (formData?.numChildren || 0))
                )}
              </td>
              <td>
                {(formData?.numAdults || 0) + (formData?.numChildren || 0)}
              </td>
              <td className="text-right">
                {currencyFormat(formData?.amount || 0)}
              </td>
            </tr>
            {payments?.map((item) => (
              <tr key={item.id}>
                <td></td>
                <td>{item.payment_type}</td>
                <td></td>
                <td></td>
                <td className="text-right flex space-x-2">
                  <span>-</span>
                  <span>{currencyFormat(item.amount || 0)}</span>
                </td>
              </tr>
            ))}
            <tr>
              <td className="text-center">{`${formatDate(
                formData?.endDate
              )}`}</td>
              <td className="font-bold">
                BALANCE TO BE PAID UPON ARRIVAL (VIA CASH OR ONLINE PAYMENT)
              </td>
              <td></td>
              <td></td>
              <td className="text-right">
                {currencyFormat(
                  (formData?.amount || 0) - (totalPaid || 0) || 0
                )}
              </td>
            </tr>
          </table>
        </section>
        <section>
          <div className="w-full text-[0.8em] border border-black">
            <div>
              <div className="flex bg-gray-900 text-white px-4">
                <div className="py-1 text-left font-bold">
                  PACKAGE INCLUSIONS
                </div>
              </div>
            </div>
            <div className="border border-gray-400 bg-white px-4 py-2 font-semibold">
              <ul className="mb-2">
                {formData?.packageInclusions?.map((item, index) => (
                  <li key={index}>
                    <div>
                      <p className="font-normal text-black">{item.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
        <section>
          <table className="w-full text-[0.8em] border border-black">
            <thead>
              <tr className="bg-gray-900 text-white">
                <th className="px-4 py-1 text-left font-bold">ITINERARY</th>
                <th className="px-4 py-1 text-left font-bold"></th>
              </tr>
            </thead>
            <tbody className="border border-gray-400">
              {dayOptions.map((item, index) => (
                <tr key={index} className="bg-white">
                  <td className="px-4 py-2 font-semibold align-top">
                    {item?.label}
                  </td>
                  <td className="px-4 py-2 font-semibold">
                    {formData?.activities?.map((activity, actIndex) => (
                      <div key={actIndex}>
                        {activity?.day === item.value && (
                          <div>
                            <h6>{activity?.name}</h6>
                            <p className="font-normal">
                              {activity?.description}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="text-xs my-5">
          <div className="font-bold">TERMS AND CONDITIONS</div>
          <ol className="list-decimal pl-4">
            <li>
              Itinerary is subject to change based on weather conditions, as
              well as time management and the cooperation of guests.
            </li>
            <li>
              Changes initiated by guests, such as alterations to the itinerary
              or the number of persons, are permissible if made at least one
              week before the start of the original tour date, subject to
              additional fees if applicable.
            </li>
            <li>
              All hotel and/or tour schedules are arranged ahead of time, thus
              down payment is strictly non-refundable.
            </li>
            <li>
              No refund will be given for any portions of the tour unused by the
              Client regardless of circumstances.
            </li>
            <li>
              Guests may reschedule day tours if the request is made at least
              one week before the start of the original tour date.
            </li>
            <p>
              For accommodation rebooking, the Hotel's terms and conditions will
              apply.
            </p>
            <li>
              In the event of natural calamities, force majeure, or
              government-mandated cancellations, day tours can be subject to
              rebooking or a refund, less expenses incurred in preparation for
              the tour. Rebooking is permitted within one year from the original
              tour date.
            </li>
            <p>
              Accomodation rebooking is always subject to Hotel's terms and
              conditions.
            </p>
          </ol>
        </section>
        <section className="text-xs my-5">
          <p>Issued by:</p>
          <div className="mt-10 text-xs">
            <p className="text-md">NIKKI CHARMAINE REAMBONANZA-ENERIO</p>
            <p className="font-bold">Happy Trails Tours and Travel</p>
            <p>DTI No. 4386732</p>
            <p>Business Permit No. BP2025-01468-0</p>
            <p>DOT Accreditation No. DOT-R07-TTA-03528-2024</p>
          </div>
        </section>
      </div>
      <div>
        <img
          src={appAsset.previewFooter}
          alt="TravelVoucher Footer"
          className="w-full py-2 bg-[#262626]"
        />
      </div>
    </div>
  );
}

export default TravelVoucherFile;
